import { ChevronDown } from 'lucide-react'
import { useEffect, useState } from 'react'

import { useEventCallback } from '@/hooks/use-event-callback'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover'
import { Separator } from '@/components/ui/separator'
import { cn } from '@/lib/utils'

export interface NumericRangeValue {
    min: number | undefined
    max: number | undefined
}

export interface NumericRangeFilterProps {
    label: string
    value: NumericRangeValue
    onChange: (next: NumericRangeValue) => void
    /** Rendered in the field and in the summary, e.g. `'$'`. */
    prefix?: string
    step?: number
    debounceMs?: number
    className?: string
}

function toDraft(value: number | undefined): string {
    return value === undefined || Number.isNaN(value) ? '' : String(value)
}

function fromDraft(draft: string): number | undefined {
    const trimmed = draft.trim()
    if (trimmed === '') return undefined
    const parsed = Number(trimmed)
    return Number.isFinite(parsed) ? parsed : undefined
}

export function NumericRangeFilter({
    label,
    value,
    onChange,
    prefix,
    step = 0.01,
    debounceMs = 400,
    className
}: NumericRangeFilterProps) {
    const [open, setOpen] = useState(false)
    const commit = useEventCallback(onChange)

    // Re-seeded from the incoming value as it changes, so a cleared chip or a
    // restored saved view is reflected in the fields without a stale frame.
    const [state, setState] = useState({ value, draft: { min: toDraft(value.min), max: toDraft(value.max) } })
    if (state.value.min !== value.min || state.value.max !== value.max) {
        setState({ value, draft: { min: toDraft(value.min), max: toDraft(value.max) } })
    }
    const draft = state.draft
    const setDraft = (next: { min: string; max: string }) => setState({ value, draft: next })

    // Typing "1", "12", "125" should not fire three queries, and a partially typed
    // number is usually not the number the operator meant.
    useEffect(() => {
        const nextMin = fromDraft(draft.min)
        const nextMax = fromDraft(draft.max)
        if (nextMin === value.min && nextMax === value.max) return

        const timer = window.setTimeout(() => commit({ min: nextMin, max: nextMax }), debounceMs)
        return () => window.clearTimeout(timer)
    }, [commit, draft, value.min, value.max, debounceMs])

    const active = value.min !== undefined || value.max !== undefined
    const summary = active
        ? `${prefix ?? ''}${value.min ?? '0'} \u2013 ${value.max === undefined ? 'any' : `${prefix ?? ''}${value.max}`}`
        : null

    return (
        <Popover open={open} onOpenChange={setOpen}>
            <PopoverTrigger asChild>
                <Button
                    variant="outline"
                    size="sm"
                    aria-label={`${label} range filter`}
                    // Cluster, not an isolated control: grown on a coarse pointer to
                    // match the other filter triggers instead of hit-slopped.
                    className={cn('justify-between gap-1.5 pointer-coarse:h-10', className)}>
                    <span className={active ? 'text-foreground' : 'text-muted-foreground'}>{label}</span>
                    {summary && (
                        <>
                            <Separator orientation="vertical" className="h-3.5" />
                            <span className="tabular text-foreground">{summary}</span>
                        </>
                    )}
                    <ChevronDown aria-hidden className="size-3.5 text-subtle-foreground" />
                </Button>
            </PopoverTrigger>

            <PopoverContent align="start" className="w-56">
                <div className="flex flex-col gap-2">
                    <div className="flex flex-col gap-1">
                        <Label htmlFor={`${label}-min`}>Minimum</Label>
                        <Input
                            id={`${label}-min`}
                            type="number"
                            inputMode="decimal"
                            step={step}
                            min={0}
                            value={draft.min}
                            onChange={event => setDraft({ ...draft, min: event.target.value })}
                            className="tabular"
                        />
                    </div>
                    <div className="flex flex-col gap-1">
                        <Label htmlFor={`${label}-max`}>Maximum</Label>
                        <Input
                            id={`${label}-max`}
                            type="number"
                            inputMode="decimal"
                            step={step}
                            min={0}
                            value={draft.max}
                            onChange={event => setDraft({ ...draft, max: event.target.value })}
                            className="tabular"
                        />
                    </div>

                    {active && (
                        <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => {
                                setDraft({ min: '', max: '' })
                                onChange({ min: undefined, max: undefined })
                            }}
                            className="pointer-coarse:h-10">
                            Clear
                        </Button>
                    )}
                </div>
            </PopoverContent>
        </Popover>
    )
}
